import { useEffect, useRef, useState, type ReactNode, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import ValueAssemblyAnimation from "../../components/ValueAssemblyAnimation";
import { CONNECTOR_SVG, HERO_SVG } from "./art";
import { Icon } from "./icons";

/** Existing booking link used by the previous landing page's "Book a demo" button. */
export const EXPERTS_URL = "https://calendly.com/ojanjooa";

const reveal = "rl-reveal";

/** True once the element has scrolled into view (immediately for reduced motion or no IntersectionObserver). */
export function useInView<T extends HTMLElement>(threshold: number) {
  const ref = useRef<T>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || !("IntersectionObserver" in window)) {
      setActive(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActive(true);
          io.disconnect();
        }
      },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  return [ref, active] as const;
}

function ExpertsButton({ ghost = false }: { ghost?: boolean }) {
  return (
    <a
      href={EXPERTS_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`rl-btn ${ghost ? "rl-btn--ghost" : "rl-btn--primary"}`}
    >
      Talk with our experts
    </a>
  );
}

function CtaPair() {
  return (
    <div className="rl-btns">
      <ExpertsButton />
      <Link to="/signin" className="rl-btn rl-btn--ghost">
        Sign in
      </Link>
    </div>
  );
}

function SectionHead({
  pill,
  title,
  lead,
  left = false,
}: {
  pill: string;
  title: ReactNode;
  lead?: string;
  left?: boolean;
}) {
  return (
    <header className={`rl-head ${left ? "rl-head--left" : ""} ${reveal}`}>
      <span className="rl-pill">{pill}</span>
      {title}
      {lead && <p className="rl-lead">{lead}</p>}
    </header>
  );
}

/* ---------------------------------------------------------------- Hero */

export function Hero() {
  return (
    <section className="rl-section rl-hero" id="home" aria-labelledby="rl-h1">
      <div className="rl-wrap rl-hero__grid">
        <div className={`rl-hero__copy ${reveal}`}>
          <span className="rl-eyebrow">ReImagine 2.0</span>
          <h1 className="rl-h1" id="rl-h1">
            Transforming data into <span className="rl-hl">competitive advantage</span> with AI-powered insights
          </h1>
          <p className="rl-lead">
            ReImagine 2.0 is our AI-powered, expert-led assessment. It scores your data + AI maturity, benchmarks you
            against your industry and turns the gaps into a prioritized, ROI-linked roadmap.
          </p>
          <CtaPair />
        </div>
        <div className={`rl-obj ${reveal}`}>
          <div dangerouslySetInnerHTML={{ __html: HERO_SVG }} />
          <span className="rl-tag" style={{ right: "0%", top: "14%" }}>
            BASELINE TO ROADMAP
          </span>
          <span className="rl-tag" style={{ left: "-4%", bottom: "16%" }}>
            AI + DOMAIN ADVISORS
          </span>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- What */

const WHAT_ITEMS = [
  {
    icon: "platform",
    title: "AI platform",
    body: "Captures inputs, scores maturity and benchmarks you against your industry, all in one workspace.",
  },
  {
    icon: "lab",
    title: "AI Innovation Lab",
    body: "Tests and proves high-value use cases quickly, before you invest at scale.",
  },
  {
    icon: "advisor",
    title: "Domain advisors",
    body: "Industry experts who validate the findings and shape a roadmap your business can act on.",
  },
];

export function What() {
  return (
    <section className="rl-section rl-dark" id="what" aria-labelledby="rl-what-h2">
      <div className="rl-wrap rl-stack">
        <SectionHead
          pill="What is ReImagine 2.0"
          title={
            <h2 className="rl-h2" id="rl-what-h2">
              A modern method to turn data + AI ambition into <span className="rl-hl">value</span>
            </h2>
          }
          lead="Our AI platform and AI Innovation Lab, guided by domain advisors, lead you to a data-driven enterprise."
        />
        <div className={`rl-what ${reveal}`}>
          <div className="rl-orb">
            <ValueAssemblyAnimation />
          </div>
          <ul className="rl-list">
            {WHAT_ITEMS.map((item) => (
              <li key={item.title} className="rl-card-dark">
                <span className="rl-ico">
                  <Icon name={item.icon} size={22} />
                </span>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------- How */

const DOMAINS = [
  { icon: "target", title: "Strategy", chips: ["Data + AI vision", "Business value & ROI", "Case for action"] },
  { icon: "team", title: "Organization", chips: ["Skills & roles", "Operating model", "Change readiness"] },
  { icon: "chip", title: "Technical", chips: ["Data + AI architecture", "Data quality", "Platforms & tools"] },
];

const OUTCOMES = ["Prioritized, ROI-linked roadmap", "Industry benchmark", "Quick wins to start now"];

const STEPS = [
  { icon: "flag", title: "Initiate", body: "Set scope, goals and stakeholders with our advisors." },
  { icon: "chat", title: "Inputs", body: "Teams share inputs on readiness through guided questions and interviews." },
  { icon: "bulb", title: "Insights", body: "AI scores maturity, benchmarks peers and surfaces the gaps." },
  { icon: "map", title: "Roadmap", body: "A prioritized plan your leadership can act on, with clear quick wins." },
];

/** Digit roller that plays once when scrolled into view. */
function Ticker({ value, suffix }: { value: number; suffix: string }) {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) {
      el?.classList.add("is-in");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const digits = Array.from({ length: 20 }, (_, i) => i % 10);
  return (
    <span className="rl-tkw" ref={ref}>
      <span className="rl-sr">
        {value} {suffix}
      </span>
      <span aria-hidden="true">
        <span className="rl-tk">
          <span className="rl-tr" style={{ ["--d" as string]: value }}>
            {digits.map((d, i) => (
              <span key={i}>{d}</span>
            ))}
          </span>
        </span>
        <span className="rl-suffix">&nbsp;{suffix}</span>
      </span>
    </span>
  );
}

export function How() {
  const [flowRef, flowActive] = useInView<HTMLDivElement>(0.35);

  return (
    <section className="rl-section rl-section--light" id="how" aria-labelledby="rl-how-h2">
      <div className="rl-wrap rl-stack">
        <SectionHead
          pill="How it works"
          title={
            <h2 className="rl-h2" id="rl-how-h2">
              From baseline maturity to <span className="rl-hl">measurable outcomes</span>
            </h2>
          }
          lead="We align your data + AI ambition with strategy, organizational and technical readiness."
        />
        <div className={reveal}>
        <div ref={flowRef} className={`rl-flowwrap${flowActive ? " is-active" : ""}`}>
          <div className="rl-baseline" aria-hidden="true">
            <span>Baseline</span>
            <span className="rl-baseline__meter">
              <span />
            </span>
          </div>
          <div className="rl-flow">
          <ul className="rl-dms">
            {DOMAINS.map((d, i) => (
              <li
                key={d.title}
                className="rl-dm rl-card-light"
                style={{ ["--d" as string]: `${(0.1 + i * 0.25).toFixed(2)}s` }}
              >
                <div className="rl-dm__head">
                  <span className="rl-ico">
                    <Icon name={d.icon} />
                  </span>
                  <h3>{d.title}</h3>
                </div>
                <ul className="rl-chips">
                  {d.chips.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
          <div dangerouslySetInnerHTML={{ __html: CONNECTOR_SVG }} style={{ display: "contents" }} />
          <div className="rl-out">
            <span className="rl-out__orb">
              <i className="rl-halo" />
              <i className="rl-halo rl-halo--late" />
              <span className="rl-out__core">Outcomes</span>
            </span>
            <ul>
              {OUTCOMES.map((o, i) => (
                <li key={o} style={{ ["--d" as string]: `${(0.9 + i * 0.2).toFixed(2)}s` }}>
                  <Icon name="check" strokeWidth={2.4} />
                  {o}
                </li>
              ))}
            </ul>
          </div>
        </div>
          </div>
        </div>
        <ol className={`rl-steps ${reveal}`}>
          {STEPS.map((s, i) => (
            <li key={s.title} className="rl-step" style={{ ["--i" as string]: i }}>
              <div className="rl-step__top">
                <span className="rl-step__ico">
                  <Icon name={s.icon} />
                </span>
                <span className="rl-step__n">{String(i + 1).padStart(2, "0")}</span>
              </div>
              <h4>{s.title}</h4>
              <p>{s.body}</p>
            </li>
          ))}
        </ol>
        <div className={`rl-speed ${reveal}`}>
          <span className="rl-speed__k">
            <span>
              <Icon name="bolt" />
            </span>
            Built for speed
          </span>
          <i className="rl-speed__sep" aria-hidden="true" />
          <b>
            <Ticker value={6} suffix="weeks" />
          </b>
          <small>from kickoff to roadmap, vs 3–6 months for typical strategy work</small>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ Features */

const WAVE = [30, 55, 80, 45, 95, 60, 35, 70, 90, 50, 40, 75, 55, 85, 30, 65, 45, 80, 60, 35, 70, 50, 90, 40];

const ROADMAP_ROWS = [
  { label: "Data governance", note: "CDO · Q1", s: "0", w: "40%" },
  { label: "Demand forecasting", note: "Ops · Q2", s: "22%", w: "40%" },
  { label: "GenAI copilot", note: "CIO · Q3", s: "52%", w: "44%" },
  { label: "MLOps platform", note: "Data team · Q2", s: "30%", w: "56%" },
];

const QUOTE = "We still reconcile production numbers by hand every Monday\u2026";

const INDUSTRIES = [
  { name: "Oil & gas", scope: "Upstream, midstream, downstream" },
  { name: "Manufacturing", scope: "Plant, supply chain, quality" },
  { name: "Distribution", scope: "Warehouse, fleet, last mile" },
];

/** Inline custom property helper for staggered delays. */
const vars = (v: Record<string, string | number>) => v as CSSProperties;

const FEATURES: { icon: string; title: string; body: string; visual: ReactNode }[] = [
  {
    icon: "team",
    title: "Collaborative workspace",
    body: "Leadership and teams share inputs, comment and align on priorities in one secure space.",
    visual: (
      <div className="rl-vx">
        <div className="rl-av-row">
          <div className="rl-av">
            {["CIO", "CDO", "OPS", "+6"].map((a, i) => (
              <span key={a} style={vars({ "--k": i })}>
                {a}
              </span>
            ))}
          </div>
          <span className="rl-live">
            <i />9 online
          </span>
        </div>
        <div className="rl-cm" style={vars({ "--t": "0.6s" })}>
          <b>Strategy lead</b>
          <p>Our top priority is production forecasting.</p>
        </div>
        <div className="rl-cm is-me" style={vars({ "--t": "1.8s" })}>
          <b>Data team</b>
          <p>Agreed. Historian data needs cleanup first.</p>
        </div>
        <div className="rl-cm" style={vars({ "--t": "3s" })}>
          <b>Advisor</b>
          <p>Added as a quick win on the roadmap.</p>
          <span className="rl-ok" style={vars({ "--t": "3.7s" })}>
            <Icon name="check" size={12} strokeWidth={3} />
            Added to roadmap
          </span>
        </div>
      </div>
    ),
  },
  {
    icon: "mic",
    title: "Whisper-based transcription",
    body: "Extracts insights from interviews and inputs, and overlays them with industry data + AI trends and peer benchmarking.",
    visual: (
      <div className="rl-vx">
        <div className="rl-rec">
          <i />
          Transcribing interview
        </div>
        <div className="rl-wv" aria-hidden="true">
          {WAVE.map((h, i) => (
            <i key={i} style={vars({ "--h": h, "--d": (i * 0.08).toFixed(2) + "s" })} />
          ))}
        </div>
        <div className="rl-ln">
          <span>Interview · Plant manager</span>
          <p>
            &ldquo;
            {QUOTE.split(" ").map((w, i) => (
              <span key={i} className="rl-word" style={vars({ "--w": i })}>
                {w}{" "}
              </span>
            ))}
            &rdquo;
          </p>
        </div>
        <div className="rl-ins" style={vars({ "--t": "2.4s" })}>
          <span>Insight</span>Manual reporting · peers automate this at Level 3
        </div>
      </div>
    ),
  },
  {
    icon: "layers",
    title: "Customizable data + AI accelerators",
    body: "Start from industry-specific frameworks, question sets and use-case libraries tuned to your business.",
    visual: (
      <div className="rl-vx">
        <div className="rl-tp">
          {INDUSTRIES.map((ind, i) => (
            <span key={ind.name} className={i === 1 ? "is-on" : ""} style={vars({ "--k": i })}>
              {ind.name}
            </span>
          ))}
        </div>
        <ul className="rl-ac">
          <li style={vars({ "--t": "0.3s" })}>
            <b>Maturity framework</b>
            <small className="rl-areas">
              {[0, 1, 2, 3, 4, 5].map((n) => (
                <i key={n} style={vars({ "--k": n })} />
              ))}
              Six maturity areas
            </small>
          </li>
          <li style={vars({ "--t": "0.6s" })}>
            <b>Use-case library</b>
            <small className="rl-cyc">
              {INDUSTRIES.map((ind, i) => (
                <span key={ind.name} style={vars({ "--k": i })}>
                  {ind.scope}
                </span>
              ))}
            </small>
          </li>
          <li style={vars({ "--t": "0.9s" })}>
            <b>Benchmark set</b>
            <small>Peer comparisons by segment</small>
          </li>
        </ul>
      </div>
    ),
  },
  {
    icon: "map",
    title: "Value-driven roadmaps",
    body: "Turn findings into a prioritized, ROI-linked roadmap with owners, timelines and quick wins.",
    visual: (
      <div className="rl-vx">
        <div className="rl-rh">
          <span>Now</span>
          <span>Next</span>
          <span>Later</span>
        </div>
        {ROADMAP_ROWS.map((r, i) => (
          <div className="rl-rr" key={r.label} style={vars({ "--k": i })}>
            <b>{r.label}</b>
            <i style={vars({ "--s": r.s, "--w": r.w })}>
              <em>{r.note}</em>
            </i>
          </div>
        ))}
        <span className="rl-win">
          <Icon name="bolt" size={12} />
          Quick win · start now
        </span>
      </div>
    ),
  },
];

export function Features() {
  const [tab, setTab] = useState(0);
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const onKey = (e: React.KeyboardEvent) => {
    const last = FEATURES.length - 1;
    let next = tab;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") next = tab === last ? 0 : tab + 1;
    else if (e.key === "ArrowUp" || e.key === "ArrowLeft") next = tab === 0 ? last : tab - 1;
    else return;
    e.preventDefault();
    setTab(next);
    refs.current[next]?.focus();
  };

  return (
    <section className="rl-section rl-dark" id="features" aria-labelledby="rl-feat-h2">
      <div className="rl-wrap rl-stack">
        <SectionHead
          pill="Powerful features"
          title={
            <h2 className="rl-h2" id="rl-feat-h2">
              Everything you need to <span className="rl-hl">assess, plan and act</span>
            </h2>
          }
          lead="The capabilities that make ReImagine 2.0 your data + AI transformation partner."
        />
        <div className={`rl-feat ${reveal}`}>
          <div className="rl-tabs" role="tablist" aria-label="Features" aria-orientation="vertical" onKeyDown={onKey}>
            {FEATURES.map((f, i) => (
              <button
                key={f.title}
                ref={(el) => {
                  refs.current[i] = el;
                }}
                type="button"
                role="tab"
                id={`rl-tab-${i}`}
                aria-selected={tab === i}
                aria-controls={`rl-tabpanel-${i}`}
                tabIndex={tab === i ? 0 : -1}
                className="rl-tab"
                onClick={() => setTab(i)}
              >
                <span className="rl-tab__n">{String(i + 1).padStart(2, "0")}</span>
                <span className="rl-tab__t">{f.title}</span>
                <span className="rl-tab__a">
                  <Icon name="arrow" />
                </span>
              </button>
            ))}
          </div>
          {/* Every panel sits in the same grid cell, so the box is always as tall as the tallest one. */}
          <div className="rl-panels">
            {FEATURES.map((f, i) => (
              <article
                key={f.title}
                className={`rl-panel${tab === i ? " is-active" : ""}`}
                id={`rl-tabpanel-${i}`}
                role="tabpanel"
                aria-labelledby={`rl-tab-${i}`}
              >
                <div className="rl-panel__top">
                  <span className="rl-ico rl-ico--lg">
                    <Icon name={f.icon} size={24} />
                  </span>
                  <div>
                    <h3>{f.title}</h3>
                    <p>{f.body}</p>
                  </div>
                </div>
                {f.visual}
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------- AI expert */

interface ChatMsg {
  from: "a" | "u";
  time: string;
  /** pause before this message starts (ms) */
  wait: number;
  /** AI messages show a typing indicator for this long first (ms) */
  typing?: number;
  body: ReactNode;
}

const CHAT: ChatMsg[] = [
  {
    from: "u",
    time: "08:34",
    wait: 600,
    body: "I want to assess our data governance maturity. Where do we stand?",
  },
  {
    from: "a",
    time: "08:34",
    wait: 500,
    typing: 1300,
    body: (
      <span className="rl-msg__h">
        <Icon name="shield" />
        Based on your inputs, your governance maturity is at Level 2: defined but inconsistently applied.
      </span>
    ),
  },
  {
    from: "u",
    time: "08:34",
    wait: 1500,
    body: "Can you evaluate our metadata management practices?",
  },
  {
    from: "a",
    time: "08:35",
    wait: 500,
    typing: 900,
    body: (
      <span className="rl-msg__h">
        <Icon name="search" />
        Scanning metadata policies, catalog usage and lineage
        <span className="rl-dots">
          <i />
          <i />
          <i />
        </span>
      </span>
    ),
  },
  {
    from: "a",
    time: "08:35",
    wait: 2000,
    body: (
      <>
        <span className="rl-msg__h">
          <Icon name="bulb" />
          Here&rsquo;s what I found.
        </span>
        <div className="rl-res">
          <div className="rl-res__t">
            Metadata management<b>Level 2</b>
          </div>
          <div className="rl-meter">
            <i className="is-full" />
            <i className="is-full" />
            <i />
            <i />
            <i />
          </div>
          <p>Next step: assign data owners and automate lineage capture. Added to your roadmap as a quick win.</p>
        </div>
      </>
    ),
  },
];

/** A conversation that starts empty, adds each message in turn (with a typing indicator), holds, then clears and repeats. */
function ChatDemo() {
  const [ref, inView] = useInView<HTMLDivElement>(0.3);
  const [shown, setShown] = useState(0);
  const [typing, setTyping] = useState(false);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (!inView) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setShown(CHAT.length);
      return;
    }

    let cancelled = false;
    const timers: number[] = [];
    const later = (ms: number, fn: () => void) => {
      timers.push(
        window.setTimeout(() => {
          if (!cancelled) fn();
        }, ms),
      );
    };

    const play = (i: number) => {
      if (i >= CHAT.length) {
        // Hold the finished conversation, fade it out, then start again from a blank chat.
        later(6000, () => {
          setFading(true);
          later(500, () => {
            setShown(0);
            setFading(false);
            later(800, () => play(0));
          });
        });
        return;
      }
      const msg = CHAT[i];
      later(msg.wait, () => {
        const show = () => {
          setTyping(false);
          setShown(i + 1);
          play(i + 1);
        };
        if (msg.typing) {
          setTyping(true);
          later(msg.typing, show);
        } else {
          show();
        }
      });
    };

    play(0);
    return () => {
      cancelled = true;
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [inView]);

  return (
    <div ref={ref} className={`rl-chat__body${fading ? " is-fading" : ""}`}>
      {CHAT.slice(0, shown).map((m, i) => (
        <div key={i} className={`rl-msg ${m.from === "a" ? "rl-msg--a" : "rl-msg--u"}`}>
          {m.body}
          <small>{m.time}</small>
        </div>
      ))}
      {typing && (
        <div className="rl-msg rl-msg--a rl-msg--typing" aria-hidden="true">
          <span className="rl-dots">
            <i />
            <i />
            <i />
          </span>
        </div>
      )}
    </div>
  );
}

export function AiSme() {
  return (
    <section className="rl-section rl-section--light" id="ai-sme" aria-labelledby="rl-sme-h2">
      <div className="rl-wrap rl-sme">
        <div className={`rl-sme__copy ${reveal}`}>
          <span className="rl-pill">AI subject matter expert</span>
          <h2 className="rl-h2" id="rl-sme-h2">
            Faster insights. <span className="rl-hl">Decisive outcomes.</span>
          </h2>
          <p className="rl-lead">
            Transforming data into milestones. Ask in plain language and get maturity scores, evidence and next steps in
            seconds.
          </p>
          <CtaPair />
        </div>
        <div
          className={`rl-chat rl-card-light ${reveal}`}
          role="img"
          aria-label="Example conversation with the ReImagine 2.0 AI subject matter expert"
        >
          <div className="rl-chat__head">
            <span className="rl-chat__av">
              <Icon name="sparkle" />
            </span>
            <div>
              <b>AI SME</b>
              <small>
                <i />
                Online · Active
              </small>
            </div>
          </div>
          <ChatDemo />
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------- FAQ */

const FAQS = [
  {
    q: "What is ReImagine 2.0 and why should my organization use it?",
    a: "ReImagine 2.0 is an AI-powered, expert-led assessment. It scores your data + AI maturity, benchmarks you against your industry and turns the gaps into a prioritized, ROI-linked roadmap, so you know where to invest first.",
  },
  {
    q: "Who is ReImagine 2.0 designed for?",
    a: "Business and technology leaders who need a shared, evidence-based view of data + AI readiness, together with the teams who run strategy, data and operations day to day.",
  },
  {
    q: "How does ReImagine 2.0 help align AI strategy across leadership and teams?",
    a: "Leadership and teams contribute in one collaborative workspace. Their priorities and on-the-ground realities are captured side by side, then reconciled into a single roadmap everyone can stand behind.",
  },
  {
    q: "How long does it take to complete an assessment?",
    a: "Most assessments move from kickoff to roadmap in about six weeks, compared with three to six months for typical strategy work.",
  },
  {
    q: "How does ReImagine 2.0 ensure accuracy in AI maturity assessment?",
    a: "AI analysis of your inputs is combined with industry benchmarks and peer comparisons, then validated by our domain advisors before any finding reaches your roadmap.",
  },
  {
    q: "What kind of reports does ReImagine 2.0 generate?",
    a: "A maturity scorecard across strategy, organizational and technical readiness, industry benchmarking, a business value and ROI view, a case for action and a prioritized roadmap with quick wins.",
  },
  {
    q: "How does the ReImagine 2.0 assessment process work?",
    a: "Four steps: initiate the scope with our advisors, gather inputs from your teams, generate AI-driven insights and benchmarks, and deliver a prioritized roadmap.",
  },
  {
    q: "How is my organization’s data secured in ReImagine 2.0?",
    a: "Your inputs stay in a secure, access-controlled workspace that only your invited users and our engagement team can see. Our experts can walk you through the full security and data-handling details.",
  },
  {
    q: "How does ReImagine 2.0 support AI governance and compliance?",
    a: "It assesses your AI governance maturity, including policies, model risk and responsible AI practices, and builds the gaps into your roadmap as clear, owned actions.",
  },
  {
    q: "How do I get started?",
    a: "Talk with our experts to scope your assessment, or sign in if your organization already has access.",
  },
];

export function Faq() {
  return (
    <section className="rl-section rl-dark" id="faq" aria-labelledby="rl-faq-h2">
      <div className="rl-wrap rl-stack">
        <SectionHead
          pill="FAQ"
          title={
            <h2 className="rl-h2" id="rl-faq-h2">
              ReImagine 2.0 <span className="rl-hl">questions, answered</span>
            </h2>
          }
          lead="Straight answers on scope, timelines, reports and security."
        />
        <div className={`rl-faq ${reveal}`}>
          {FAQS.map((item, i) => (
            <details className="rl-q" key={item.q} open={i === 0}>
              <summary>
                <span className="rl-q__n">{String(i + 1).padStart(2, "0")}</span>
                <h3>
                  <span>{item.q}</span>
                </h3>
                <span className="rl-q__ic">
                  <Icon name="plus" strokeWidth={2.2} />
                </span>
              </summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------- CTA band */

export function Cta() {
  return (
    <section className="rl-section rl-cta" aria-labelledby="rl-cta-h2" style={{ minHeight: 0 }}>
      <div className="rl-wrap">
        <div className={`rl-band ${reveal}`}>
          <span aria-hidden="true" className="rl-ring rl-ring--1" />
          <span aria-hidden="true" className="rl-ring rl-ring--2" />
          <h2 className="rl-h2" id="rl-cta-h2">
            Ready to define your <span className="rl-hl">path to AI</span>?
          </h2>
          <p className="rl-lead">Talk with our experts about your data + AI maturity, priorities and roadmap.</p>
          <CtaPair />
        </div>
      </div>
    </section>
  );
}
